import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';

import OrderStatusChip from './OrderStatusChip';
import { ProjectOrder } from '../data/projectsData';
import {
  PHASE_HEADER_COLORS,
  WORK_TYPE_LABELS,
  assignmentsForOrder,
  departmentLabel,
  formatDateDisplay,
  jobById,
  scheduledDateRange,
  scheduledHours,
} from '../utils/projectsUtils';

interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
}

const SummaryRow: FC<SummaryRowProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} component="div">
      {value}
    </Typography>
  </Box>
);

interface SchedulingGroupCardProps {
  order: ProjectOrder;
  expanded: boolean;
  onToggleExpand: () => void;
  onShowHistory: (orderNo: string) => void;
}

const SchedulingGroupCard: FC<SchedulingGroupCardProps> = ({ order, expanded, onToggleExpand, onShowHistory }) => {
  const history = useHistory();

  const assignments = assignmentsForOrder(order.order_no);
  const range = scheduledDateRange(assignments);
  const crew = Array.from(new Set(assignments.map((assignment) => assignment.tech_name)));
  const scheduledDays = new Set(assignments.map((assignment) => assignment.date)).size;
  const headerColor = PHASE_HEADER_COLORS[order.work_type] || '#546e7a';
  const phaseLabel = WORK_TYPE_LABELS[order.work_type] || order.work_type;

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ backgroundColor: headerColor, color: 'white', px: 2, py: 1, textAlign: 'center' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {phaseLabel} — #{order.order_no}
        </Typography>
        <Typography variant="body2">
          {range.first
            ? `${formatDateDisplay(range.first)} – ${formatDateDisplay(range.last)} (${scheduledDays} day${scheduledDays === 1 ? '' : 's'})`
            : 'Not yet scheduled'}
        </Typography>
      </Box>

      <Box sx={{ p: 2, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
          <OrderStatusChip status={order.status} />
          {order.unscheduled && <Chip label="Unscheduled" size="small" color="warning" variant="outlined" />}
        </Box>

        <Grid container spacing={expanded ? 3 : 0}>
          {/* Summary — full card width when collapsed, left column when expanded */}
          <Grid item xs={12} md={expanded ? 5 : 12}>
            <SummaryRow label="Department" value={departmentLabel(jobById(order.job_id)?.dept_id || '')} />
            <SummaryRow label="Lead Tech" value={order.lead_tech || '—'} />
            <SummaryRow label="Crew" value={crew.length > 0 ? `${crew.length} technicians` : '—'} />
            <SummaryRow label="Arrival Time" value={assignments[0]?.start_time || '—'} />
            <SummaryRow label="Scheduled Hours" value={scheduledHours(assignments).toLocaleString()} />
            <SummaryRow label="Created" value={formatDateDisplay(order.dt_in)} />
            <SummaryRow label="Completed" value={formatDateDisplay(order.dt_complt)} />
          </Grid>

          {/* Crew assignments — only when expanded, fills the rest of the full-width row */}
          {expanded && (
            <Grid item xs={12} md={7}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                CREW ASSIGNMENTS
              </Typography>
              {assignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No crew scheduled yet.
                </Typography>
              ) : (
                <Box
                  sx={{
                    maxHeight: 360,
                    overflowY: 'auto',
                    mt: 0.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Technician</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell align="right">Hrs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignments.map((assignment, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{formatDateDisplay(assignment.date)}</TableCell>
                          <TableCell>{assignment.tech_name}</TableCell>
                          <TableCell>{assignment.start_time}</TableCell>
                          <TableCell align="right">{assignment.duration}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider />
      <Grid container>
        <Grid item xs={4}>
          <Button
            fullWidth
            size="small"
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={onToggleExpand}
          >
            {expanded ? 'Hide' : 'Details'}
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button fullWidth size="small" startIcon={<HistoryIcon />} onClick={() => onShowHistory(order.order_no)}>
            History
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            fullWidth
            size="small"
            startIcon={<EditCalendarIcon />}
            onClick={() => history.push(`/projects/scheduling?job=${order.job_id}`)}
          >
            Schedule
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

export default SchedulingGroupCard;
